// Single place that talks to the API. Everything else calls these helpers, so
// token handling and error shapes only need getting right once.

const TOKEN_KEY = 'ndaki.token';
export { TOKEN_KEY };
const OFFLINE_QUEUE_KEY = 'ndaki.offline.queue.v1';
const OFFLINE_STATUS_EVENT = 'ndaki:offline-status';
const OFFLINE_SYNC_INTERVAL_MS = 15000;

let syncTimer = null;
let syncInFlight = null;
let lastSyncError = null;
let lastSyncedAt = null;

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (token) => {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
};

export class ApiError extends Error {
  constructor(status, message, details) {
    super(message);
    this.status = status;
    // Field-level messages from the server, keyed by form field name.
    this.details = details || null;
  }
}

const readOfflineQueue = () => {
  try {
    const raw = localStorage.getItem(OFFLINE_QUEUE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeOfflineQueue = (queue) => {
  localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(queue));
};

const dispatchOfflineStatus = () => {
  window.dispatchEvent(
    new CustomEvent(OFFLINE_STATUS_EVENT, {
      detail: getOfflineSyncStatus()
    })
  );
};

const isMutationMethod = (method) => ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);

const canQueueOffline = (method, path, formData) => {
  if (!isMutationMethod(method)) return false;
  if (formData) return false;
  // Authentication requests must always be live and immediate.
  if (path.startsWith('/auth/')) return false;
  return true;
};

const queueMutation = ({ method, path, body }) => {
  const queue = readOfflineQueue();
  const queued = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
    createdAt: new Date().toISOString(),
    method,
    path,
    body: body ?? null,
    token: getToken()
  };
  queue.push(queued);
  writeOfflineQueue(queue);
  dispatchOfflineStatus();
  return {
    queued: true,
    queuedRequestId: queued.id,
    queuedAt: queued.createdAt,
    message: 'Saved on this device. It will sync automatically when internet returns.'
  };
};

const rawFetch = async ({ method, path, body, formData, signal, tokenOverride }) => {
  const headers = {};
  const token = tokenOverride ?? getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  let response;
  try {
    response = await fetch(`/api${path}`, {
      method,
      headers,
      body: formData ?? (body !== undefined ? JSON.stringify(body) : undefined),
      signal
    });
  } catch (error) {
    if (error.name === 'AbortError') throw error;
    throw new ApiError(0, 'Cannot reach the server. Check your connection and try again.', {
      offline: !navigator.onLine
    });
  }

  if (response.status === 204) return null;

  const text = await response.text();
  let payload = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = { error: text.slice(0, 300) };
    }
  }

  if (!response.ok) {
    // An expired or revoked token should log the user out rather than leaving the
    // interface half broken.
    if (response.status === 401 && getToken()) {
      setToken(null);
      window.dispatchEvent(new Event('ndaki:signed-out'));
    }
    throw new ApiError(response.status, payload?.error || `Request failed (${response.status})`, payload?.details);
  }

  return payload;
};

const request = async (method, path, { body, formData, signal } = {}) => {
  const queueable = canQueueOffline(method, path, formData);

  if (!navigator.onLine && queueable) {
    return queueMutation({ method, path, body });
  }

  try {
    return await rawFetch({ method, path, body, formData, signal });
  } catch (error) {
    if (error.name === 'AbortError') throw error;
    if (queueable && error instanceof ApiError && error.status === 0) {
      return queueMutation({ method, path, body });
    }
    throw error;
  }
};

const replayQueuedMutation = async (item) => {
  await rawFetch({
    method: item.method,
    path: item.path,
    body: item.body,
    tokenOverride: item.token
  });
};

export const syncOfflineQueue = async () => {
  if (syncInFlight) return syncInFlight;
  if (!navigator.onLine) return getOfflineSyncStatus();

  syncInFlight = (async () => {
    let queue = readOfflineQueue();
    lastSyncError = null;
    dispatchOfflineStatus();

    while (queue.length > 0) {
      const item = queue[0];
      try {
        await replayQueuedMutation(item);
        queue = queue.slice(1);
        writeOfflineQueue(queue);
        lastSyncedAt = new Date().toISOString();
        dispatchOfflineStatus();
      } catch (error) {
        lastSyncError = error.message || 'Sync failed';
        dispatchOfflineStatus();
        break;
      }
    }

    return getOfflineSyncStatus();
  })();

  try {
    return await syncInFlight;
  } finally {
    syncInFlight = null;
    dispatchOfflineStatus();
  }
};

export const startOfflineSync = () => {
  const onOnline = () => {
    syncOfflineQueue();
  };
  window.addEventListener('online', onOnline);

  if (!syncTimer) {
    syncTimer = window.setInterval(() => {
      if (!navigator.onLine) return;
      if (readOfflineQueue().length === 0) return;
      syncOfflineQueue();
    }, OFFLINE_SYNC_INTERVAL_MS);
  }

  dispatchOfflineStatus();
  if (navigator.onLine) syncOfflineQueue();

  return () => {
    window.removeEventListener('online', onOnline);
  };
};

export const getOfflineSyncStatus = () => {
  const pendingCount = readOfflineQueue().length;
  return {
    online: navigator.onLine,
    pendingCount,
    syncing: Boolean(syncInFlight),
    lastSyncError,
    lastSyncedAt
  };
};

export const subscribeOfflineSyncStatus = (callback) => {
  const listener = (event) => callback(event.detail);
  window.addEventListener(OFFLINE_STATUS_EVENT, listener);
  callback(getOfflineSyncStatus());
  return () => window.removeEventListener(OFFLINE_STATUS_EVENT, listener);
};

export const api = {
  get: (path, options) => request('GET', path, options),
  post: (path, body, options) => request('POST', path, { ...options, body }),
  put: (path, body, options) => request('PUT', path, { ...options, body }),
  patch: (path, body, options) => request('PATCH', path, { ...options, body }),
  del: (path, options) => request('DELETE', path, options),
  upload: (path, formData, options) => request('POST', path, { ...options, formData })
};

// Turns an object into a query string, dropping empty values so the URL stays
// readable and shareable.
export const toQuery = (params) => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params || {})) {
    if (value === undefined || value === null || value === '' || value === 'any') continue;
    if (Array.isArray(value)) {
      if (value.length > 0) search.set(key, value.join(','));
    } else {
      search.set(key, String(value));
    }
  }
  const query = search.toString();
  return query ? `?${query}` : '';
};
